import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';

import { UserAssetsHttpService } from '@core/http/user-assets.http.service';
import { FileHandlerService } from '@core/services/file-handler.service';
import { SingleFileUploadResult, UserAsset } from '@interfaces';
import { StackedMessagesService, TypedStackedMessageConfig } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';
import * as reviewActions from './review.actions';

type ReviewUploadResult = {
  errorMessage: any;
  uploaded: boolean;
  userAsset: UserAsset;
};

@Injectable()
export class ReviewEffects {
  private readonly actions$ = inject(Actions);
  private readonly assetsHttp = inject(UserAssetsHttpService);
  private readonly fileHandlerService = inject(FileHandlerService);
  private readonly stackedMessages = inject(StackedMessagesService);

  private showError(config: TypedStackedMessageConfig): void {
    this.stackedMessages.showError(config);
  }

  getMyDocumentsSentToReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reviewActions.getMyDocumentsSentToReview),
      mergeMap(({ opts }) => {
        const { userProcessId } = opts;
        return this.assetsHttp.getMyDocumentsSentToReview(opts).pipe(
          map(userAssets => {
            return reviewActions.getMyDocumentsSentToReviewSuccess({ opts: { userAssets, userProcessId } });
          }),
          catchError(error => {
            this.showError({ message: 'COMMON.ERROR.ERROR_FETCHING_DOCUMENTS_SENT_TO_REVIEW' });
            const errorMessage = parseError(error, reviewActions.getMyDocumentsSentToReview.type);
            return of(reviewActions.getMyDocumentsSentToReviewError({ opts: { errorMessage, userProcessId } }));
          })
        );
      })
    )
  );

  uploadDocumentToReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reviewActions.uploadDocumentToReview),
      concatMap(({ opts }) => {
        const { userProcessId, files } = opts;
        const resultsObj: { [key: string]: Observable<SingleFileUploadResult> } = files.reduce(
          (acc, currFile) => {
            acc[currFile.fileOriginalName] = this.assetsHttp
              .uploadDocumentToReview({ userProcessId, file: currFile.file })
              .pipe(
                map((uploadResult): ReviewUploadResult => {
                  return { errorMessage: null, uploaded: true, userAsset: uploadResult };
                }),
                catchError(error => {
                  this.showError({
                    message: 'COMMON.ERROR.ERROR_UPLOADING_FILE',
                    translateParams: { fileName: currFile.fileOriginalName },
                  });
                  const errorMessage = parseError(error, reviewActions.uploadDocumentToReview.type);
                  return of<ReviewUploadResult>({
                    errorMessage,
                    uploaded: false,
                    userAsset: { fileOriginalName: currFile.fileOriginalName } as UserAsset,
                  });
                })
              );
            return acc;
          },
          {} as { [key: string]: Observable<SingleFileUploadResult> }
        );

        return forkJoin(resultsObj).pipe(
          map(uploadedFiles => {
            return reviewActions.uploadDocumentToReviewSuccess({ opts: { userProcessId, uploadedFiles } });
          }),
          catchError(err => {
            const errorMessage = parseError(err, reviewActions.uploadDocumentToReview.type);
            return of(reviewActions.uploadDocumentToReviewError({ opts: { errorMessage, userProcessId } }));
          })
        );
      })
    )
  );

  downloadMyDocumentFromReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reviewActions.downloadDocumentFromReview),
      switchMap(({ opts }) => {
        return this.assetsHttp.downloadMyAsset(opts).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey: opts.previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return reviewActions.downloadDocumentFromReviewSuccess({ opts });
            } else {
              return reviewActions.downloadDocumentFromReviewError({ opts });
            }
          }),
          catchError(error => {
            if (opts.previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(opts.previewTargetKey);
            }
            // Show notifciation with error message
            this.showError({
              message: 'NT2.ERROR_DOWNLOADING_FILE',
              translateParams: { fileName: opts.fileOriginalName },
            });
            const errorMessage = parseError(error, reviewActions.downloadDocumentFromReview.type);
            return of(reviewActions.downloadDocumentFromReviewError({ opts: { ...opts, errorMessage } }));
          })
        );
      })
    )
  );

  removeMyFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reviewActions.removeMyFile),
      switchMap(({ opts }) => {
        return this.assetsHttp.removeMyAsset(opts).pipe(
          map(() => {
            return reviewActions.removeMyFileSuccess({ opts });
          }),
          catchError(error => {
            // Show notifciation with error message
            this.showError({
              message: 'NT2.ERROR_REMOVING_FILE',
              translateParams: { fileName: opts.fileOriginalName },
            });
            const errorMessage = parseError(error, reviewActions.removeMyFile.type);
            return of(reviewActions.removeMyFileError({ opts: { ...opts, errorMessage } }));
          })
        );
      })
    )
  );
}
